import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSupplierById, fetchSupplierLedger } from '@/services/suppliers';
import { fetchShopSettings } from '@/services/settings';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Supplier } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { SupplierForm } from './SupplierForm';
import { printSupplierStatement } from '@/lib/supplierStatementPrinter';
import { useAuth } from '@/context/AuthContext';
import {
  Edit2,
  Loader2,
  Printer,
  ShieldAlert,
} from 'lucide-react';

interface SupplierDetailProps {
  supplierId: string;
  onClose: () => void;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatMoney(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const SupplierDetail: React.FC<SupplierDetailProps> = ({ supplierId, onClose }) => {
  const { role } = useAuth();
  const isManagerOrOwner = role === 'owner' || role === 'manager';
  const queryClient = useQueryClient();
  const [showEditForm, setShowEditForm] = useState(false);

  const { data: supplier, isLoading, error, refetch } = useQuery<Supplier>({
    queryKey: queryKeys.suppliers.detail(supplierId),
    queryFn: () => fetchSupplierById(supplierId),
  });

  const { data: ledger, isLoading: loadingLedger } = useQuery({
    queryKey: ['suppliers', 'ledger', supplierId],
    queryFn: () => fetchSupplierLedger(supplierId),
    enabled: !!supplier && isManagerOrOwner,
  });

  const { data: shopSettings } = useQuery({
    queryKey: ['shop-settings'],
    queryFn: () => fetchShopSettings(),
  });

  const handlePrintStatement = () => {
    if (!supplier || !ledger || !shopSettings) return;
    printSupplierStatement({
      shop: shopSettings,
      supplier,
      ledger,
    });
  };

  if (!isManagerOrOwner) {
    return (
      <div className="p-6 text-center">
        <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-2" />
        <h3 className="font-bold text-brand-black text-sm">Access Restricted</h3>
        <p className="text-xs text-brand-muted mt-1">
          Supplier accounts, purchasing costs, and payable balances are restricted to Owners and Managers.
        </p>
        <button
          onClick={onClose}
          className="mt-4 px-4 py-1.5 bg-brand-black text-white text-xs font-bold rounded"
        >
          Close
        </button>
      </div>
    );
  }

  if (showEditForm && supplier) {
    return (
      <Modal isOpen title={`Edit — ${supplier.name}`} onClose={() => setShowEditForm(false)}>
        <SupplierForm
          supplier={supplier}
          onSuccess={() => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.detail(supplierId) });
            setShowEditForm(false);
          }}
          onCancel={() => setShowEditForm(false)}
        />
      </Modal>
    );
  }

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {error && (
        <ErrorAlert
          message={parseDatabaseError(error)}
          onRetry={() => refetch()}
        />
      )}

      {supplier && !isLoading && (
        <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-1">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-brand-border">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-emerald-700 text-white flex items-center justify-center text-lg font-bold uppercase">
                {supplier.name.charAt(0)}
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-bold text-brand-black">{supplier.name}</h3>
                  {supplier.supplier_code && (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-[10px] font-mono font-bold rounded">
                      {supplier.supplier_code}
                    </span>
                  )}
                </div>
                <p className="text-xs text-brand-muted">
                  {supplier.contact_name ? `${supplier.contact_name} • ` : ''}
                  {supplier.phone || 'No phone'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={handlePrintStatement}
                disabled={!ledger || loadingLedger}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-white border border-brand-border text-brand-black text-xs font-semibold rounded-md hover:bg-brand-bg transition-colors shadow-sm"
              >
                <Printer className="w-3.5 h-3.5 text-brand-green" />
                <span>Statement</span>
              </button>

              <button
                onClick={() => setShowEditForm(true)}
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-brand-green text-white text-xs font-semibold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm"
              >
                <Edit2 className="w-3.5 h-3.5" />
                <span>Edit</span>
              </button>
            </div>
          </div>

          {/* Summary Metrics */}
          {ledger && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="p-2.5 bg-brand-bg rounded-lg border border-brand-border">
                <span className="text-[10px] uppercase font-bold text-brand-muted block">Total Purchases</span>
                <span className="text-sm font-extrabold text-brand-black">{formatMoney(ledger.totalPurchases)}</span>
                <span className="text-[10px] text-brand-muted block mt-0.5">{ledger.purchasesCount} invoice(s)</span>
              </div>

              <div className="p-2.5 bg-brand-bg rounded-lg border border-brand-border">
                <span className="text-[10px] uppercase font-bold text-brand-muted block">Total Paid Out</span>
                <span className="text-sm font-extrabold text-emerald-700">{formatMoney(ledger.totalPaid)}</span>
                <span className="text-[10px] text-brand-muted block mt-0.5">Disbursements</span>
              </div>

              <div className="p-2.5 bg-brand-bg rounded-lg border border-brand-border">
                <span className="text-[10px] uppercase font-bold text-brand-muted block">Returns / Credits</span>
                <span className="text-sm font-extrabold text-amber-700">{formatMoney(ledger.totalSupplierReturns)}</span>
                <span className="text-[10px] text-brand-muted block mt-0.5">Return adjustments</span>
              </div>

              <div className={`p-2.5 rounded-lg border ${
                ledger.outstandingPayable > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
              }`}>
                <span className={`text-[10px] uppercase font-bold block ${
                  ledger.outstandingPayable > 0 ? 'text-red-800' : 'text-green-800'
                }`}>
                  Net Outstanding Payable
                </span>
                <span className={`text-sm font-black ${
                  ledger.outstandingPayable > 0 ? 'text-red-700' : 'text-green-700'
                }`}>
                  {formatMoney(ledger.outstandingPayable)}
                </span>
                <span className="text-[10px] text-brand-muted block mt-0.5">Current Liability</span>
              </div>
            </div>
          )}

          {/* Supplier Payable Ledger Timeline */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold text-brand-black uppercase tracking-wider">
                Supplier Payable Timeline & Ledger
              </h4>
              <span className="text-[10px] text-brand-muted">
                {ledger?.entries.length || 0} transaction(s)
              </span>
            </div>

            <div className="bg-white border border-brand-border rounded-lg overflow-hidden text-xs">
              <table className="w-full text-left">
                <thead className="bg-brand-bg border-b border-brand-border text-[10px] uppercase text-brand-muted font-bold">
                  <tr>
                    <th className="p-2.5">Date</th>
                    <th className="p-2.5">Ref #</th>
                    <th className="p-2.5">Description</th>
                    <th className="p-2.5 text-right">Debit (Paid -)</th>
                    <th className="p-2.5 text-right">Credit (Purch +)</th>
                    <th className="p-2.5 text-right">Payable Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-border">
                  {loadingLedger ? (
                    <tr>
                      <td colSpan={6} className="text-center py-6 text-brand-muted">
                        <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading payable ledger…
                      </td>
                    </tr>
                  ) : !ledger || ledger.entries.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-6 text-brand-muted">
                        No transactions recorded with this supplier yet.
                      </td>
                    </tr>
                  ) : (
                    ledger.entries.map((e) => (
                      <tr key={e.id} className="hover:bg-brand-bg/50 transition-colors">
                        <td className="p-2.5 font-mono text-[11px] text-brand-muted">{formatDate(e.date)}</td>
                        <td className="p-2.5 font-mono font-bold text-brand-black">{e.reference}</td>
                        <td className="p-2.5 text-brand-black">{e.description}</td>
                        <td className="p-2.5 text-right font-medium text-emerald-700">
                          {e.debit > 0 ? formatMoney(e.debit) : '—'}
                        </td>
                        <td className="p-2.5 text-right font-medium text-brand-black">
                          {e.credit > 0 ? formatMoney(e.credit) : '—'}
                        </td>
                        <td className="p-2.5 text-right font-bold text-brand-black">
                          {formatMoney(e.runningBalance)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end pt-4 mt-4 border-t border-brand-border">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors"
        >
          Close
        </button>
      </div>
    </>
  );
};
